import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { daysFromNow } from '@zapier/date';
import { useCurrentAccountId } from '@zapier/design-system-revenue/src/hooks';

type Output = {
  error: string | undefined;
  hasSaved: boolean;
  isAutoUpgradeEnabled: boolean;
  isOnExtendedTrial: boolean;
  isLoading: boolean;
  onToggleAutoUpgrade: () => void;
  trialDaysLeft: number | undefined;
};

const autoUpgradeQuery = gql`
  query autoUpgradeQuery($accountId: Int!) {
    account(id: $accountId) {
      enabledTrialExtensions
      id
      trialAutoUpgrade
      trialEndDate
    }
  }
`;

const toggleAutoUpgradeMutation = gql`
  mutation toggleAutoUpgrade($accountId: Int!, $input: AccountInput!) {
    updateAccount(id: $accountId, input: $input) {
      enabledTrialExtensions
      id
      trialAutoUpgrade
      trialEndDate
    }
  }
`;

const getTrialDaysLeft = (trialEndDate: string) =>
  trialEndDate ? daysFromNow(trialEndDate) : undefined;

export const useAutoUpgrade = (): Output => {
  const { accountId } = useCurrentAccountId();

  const {
    loading: isLoadingAutoUpgrade,
    data: autoUpgradeData,
    error: autoUpgradeError,
  } = useQuery(autoUpgradeQuery, {
    variables: {
      accountId,
    },
  });

  const [
    toggleAutoUpgrade,
    {
      called: hasBeenToggled,
      loading: isLoadingMutation,
      data: mutationData,
      error: mutationError,
    },
  ] = useMutation(toggleAutoUpgradeMutation);

  const account = autoUpgradeData?.account;
  const mutatedAccount = mutationData?.updateAccount;
  const showMutatedData = hasBeenToggled && !isLoadingMutation;

  const onToggleAutoUpgrade = () => {
    toggleAutoUpgrade({
      variables: {
        accountId,
        input: {
          trialAutoUpgrade: !account?.trialAutoUpgrade,
        },
      },
    });
  };

  return {
    error:
      autoUpgradeError || mutationError
        ? 'There was an issue loading auto upgrade. Please try again.'
        : undefined,
    hasSaved: hasBeenToggled,
    isAutoUpgradeEnabled: showMutatedData
      ? mutatedAccount?.trialAutoUpgrade
      : account?.trialAutoUpgrade,
    isLoading: isLoadingAutoUpgrade || isLoadingMutation,
    isOnExtendedTrial: showMutatedData
      ? mutatedAccount?.enabledTrialExtensions?.length > 0
      : account?.enabledTrialExtensions?.length > 0,
    onToggleAutoUpgrade,
    trialDaysLeft: showMutatedData
      ? getTrialDaysLeft(mutatedAccount?.trialEndDate)
      : getTrialDaysLeft(account?.trialEndDate),
  };
};
